import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Get selected project type text
    const projectSelect = form.querySelector('select[name="project"]') as HTMLSelectElement;
    const projectValue = projectSelect.options[projectSelect.selectedIndex].text;
    
    // Create URL-encoded data
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('name', formData.get('name') as string);
    urlEncodedData.append('email', formData.get('email') as string);
    urlEncodedData.append('phone', formData.get('phone') as string);
    urlEncodedData.append('project', projectValue);
    urlEncodedData.append('message', formData.get('message') as string);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ojo74l56fkiit4o480', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const services = [
    {
      title: 'Site Preparation & Grading',
      description: 'Professional land clearing, grading, and site preparation for residential and commercial projects.',
      icon: 'ri-landscape-line'
    },
    {
      title: 'Trenching & Utilities',
      description: 'Expert trenching services for water lines, sewer systems, electrical conduits, and utility installations.',
      icon: 'ri-tools-line'
    },
    {
      title: 'Land Clearing & Demolition',
      description: 'Complete land clearing, brush removal, and demolition services for new construction projects.',
      icon: 'ri-delete-bin-line'
    },
    {
      title: 'Drainage Solutions',
      description: 'Custom drainage systems, retention ponds, and erosion control to protect your property.',
      icon: 'ri-water-flash-line'
    }
  ];

  const projects = [
    {
      title: 'Residential Site Development',
      category: 'Site Preparation',
      image: 'https://public.readdy.ai/ai/img_res/b81f1e50f43648d6fafae35aac40f7f7.jpg',
      location: 'Louisville, KY'
    },
    {
      title: 'Commercial Foundation Excavation',
      category: 'Excavation',
      image: 'https://readdy.ai/api/search-image?query=large%20excavator%20digging%20deep%20foundation%20trench%20for%20commercial%20building%20with%20exposed%20earth%20walls%20and%20construction%20equipment%20on%20site%20during%20daytime%20professional%20documentation%20photography%20showing%20scale%20and%20depth%20of%20excavation%20work&width=800&height=600&seq=excavate2&orientation=landscape',
      location: 'Lexington, KY'
    },
    {
      title: 'Utility Trenching Project',
      category: 'Trenching',
      image: 'https://readdy.ai/api/search-image?query=excavator%20creating%20precise%20utility%20trench%20with%20straight%20lines%20for%20underground%20pipe%20installation%20showing%20organized%20construction%20site%20with%20dirt%20piles%20and%20utility%20equipment%20professional%20construction%20documentation%20photography&width=800&height=600&seq=excavate3&orientation=landscape',
      location: 'Frankfort, KY'
    },
    {
      title: 'Land Clearing & Grading',
      category: 'Land Clearing',
      image: 'https://readdy.ai/api/search-image?query=bulldozer%20clearing%20land%20and%20removing%20trees%20and%20brush%20from%20construction%20site%20with%20freshly%20cleared%20ground%20and%20organized%20debris%20piles%20under%20natural%20daylight%20professional%20earthmoving%20equipment%20photography&width=800&height=600&seq=excavate4&orientation=landscape',
      location: 'Bowling Green, KY'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                <i className="ri-truck-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Kader Excavating</h1>
                <p className="text-xs text-gray-600">Kentucky</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-orange-600' : 'text-white hover:text-orange-300'}`}>Home</a>
              <a href="#services" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-orange-600' : 'text-white hover:text-orange-300'}`}>Services</a>
              <a href="#projects" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-orange-600' : 'text-white hover:text-orange-300'}`}>Projects</a>
              <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-orange-600' : 'text-white hover:text-orange-300'}`}>About</a>
              <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-orange-600' : 'text-white hover:text-orange-300'}`}>Contact</a>
            </div>
            <a href="#contact" className="hidden md:block px-6 py-2.5 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer">
              Get a Quote
            </a>
            <button className="md:hidden">
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=powerful%20excavator%20machine%20working%20on%20large%20construction%20site%20moving%20earth%20and%20dirt%20with%20dramatic%20sky%20background%20showing%20professional%20excavating%20equipment%20in%20action%20during%20golden%20hour%20with%20dust%20and%20activity%20professional%20heavy%20equipment%20photography&width=1920&height=1080&seq=heroeexcavate&orientation=landscape" 
            alt="Excavating equipment background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Professional Excavating<br />Services in Kentucky
          </h2>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Expert site preparation, grading, trenching, and land clearing. Serving Kentucky with reliable excavating solutions for over 15 years.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#contact" className="px-8 py-4 bg-orange-600 text-white text-base font-semibold rounded-lg hover:bg-orange-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              Request a Quote
            </a>
            <a href="#projects" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-base font-semibold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer">
              View Our Work
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">Excavating Solutions</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive excavating and earthmoving services for residential, commercial, and industrial projects throughout Kentucky.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer group border border-gray-100"
                onMouseEnter={() => setActiveService(index)}
              >
                <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-orange-600 transition-colors">
                  <i className={`${service.icon} text-3xl text-orange-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Portfolio</span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">Recent Projects</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our latest excavating projects showcasing precision earthwork and professional site development.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group cursor-pointer overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <span className="inline-block px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded-full mb-3">
                      {project.category}
                    </span>
                    <h4 className="text-2xl font-bold text-white mb-2">{project.title}</h4>
                    <p className="text-white/90 text-sm flex items-center">
                      <i className="ri-map-pin-line mr-2"></i>
                      {project.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">Kentucky's Trusted Excavating Experts</h3>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Kader Excavating has been serving Kentucky with professional excavating and earthmoving services for over 15 years. We specialize in site preparation, grading, trenching, land clearing, and drainage solutions for residential, commercial, and industrial projects.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Our experienced operators and modern fleet of excavating equipment ensure precision work, timely completion, and competitive pricing. From small residential lots to large commercial developments, we handle projects of all sizes with the same commitment to quality and safety.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">500+</div>
                  <div className="text-sm text-gray-600">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">15+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">100%</div>
                  <div className="text-sm text-gray-600">Client Satisfaction</div>
                </div>
              </div>
              <a href="#contact" className="inline-block px-8 py-3 bg-orange-600 text-white text-base font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer">
                Get Your Free Estimate
              </a>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="h-64 rounded-xl overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=excavator%20operator%20in%20safety%20gear%20working%20controls%20inside%20modern%20excavator%20cab%20with%20construction%20site%20visible%20through%20window%20professional%20documentation%20photography%20showing%20skilled%20operation%20and%20safety%20standards&width=400&height=500&seq=aboutexc1&orientation=portrait"
                      alt="Excavator operator"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="h-48 rounded-xl overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=bulldozer%20grading%20and%20leveling%20construction%20site%20with%20smooth%20dirt%20surface%20and%20precise%20earthwork%20showing%20professional%20land%20grading%20equipment%20in%20action%20during%20daytime%20with%20clear%20visibility&width=400&height=400&seq=aboutexc2&orientation=squarish"
                      alt="Grading work"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-12">
                  <div className="h-48 rounded-xl overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=excavator%20digging%20deep%20trench%20with%20precision%20showing%20clean%20straight%20walls%20and%20organized%20excavation%20work%20on%20construction%20site%20professional%20earthmoving%20equipment%20photography&width=400&height=400&seq=aboutexc3&orientation=squarish"
                      alt="Trenching project"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="h-64 rounded-xl overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=fleet%20of%20yellow%20excavating%20equipment%20including%20excavators%20bulldozers%20and%20dump%20trucks%20parked%20at%20construction%20yard%20showing%20professional%20heavy%20machinery%20and%20well-maintained%20equipment%20during%20golden%20hour&width=400&height=500&seq=aboutexc4&orientation=portrait"
                      alt="Equipment fleet"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">What Our Clients Say</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our satisfied clients have to say about our excavating services.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'John Mitchell',
                role: 'Homeowner',
                content: 'Kader Excavating did an excellent job preparing our home site. Professional, efficient, and the grading was perfect.',
                rating: 5
              },
              {
                name: 'Sarah Thompson',
                role: 'Property Developer',
                content: 'Outstanding excavating work on our commercial development. They completed the site work ahead of schedule and within budget.',
                rating: 5
              },
              {
                name: 'Michael Davis',
                role: 'General Contractor',
                content: 'Reliable and skilled operators. Kader Excavating is our go-to partner for all excavating and site preparation needs.',
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-500 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 text-base leading-relaxed mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
              <h3 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">Ready to Start Your Excavating Project?</h3>
              <p className="text-gray-300 text-base leading-relaxed mb-8">
                Contact us today for a free estimate. We'll assess your project needs and provide competitive pricing for all excavating services.
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Phone</div>
                    <a href="tel:+15025551234" className="text-white text-lg font-semibold hover:text-orange-500 transition-colors cursor-pointer">
                      (502) 555-1234
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Email</div>
                    <a href="mailto:info@kaderexcavatingky.com" className="text-white text-lg font-semibold hover:text-orange-500 transition-colors cursor-pointer">
                      info@kaderexcavatingky.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Location</div>
                    <div className="text-white text-lg font-semibold">
                      Louisville, Kentucky
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Full Name</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="(502) 555-1234"
                  />
                </div>
                <div>
                  <label htmlFor="project" className="block text-sm font-semibold text-gray-900 mb-2">Service Needed</label>
                  <select 
                    id="project"
                    name="project"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="site-prep">Site Preparation & Grading</option>
                    <option value="trenching">Trenching & Utilities</option>
                    <option value="land-clearing">Land Clearing & Demolition</option>
                    <option value="drainage">Drainage Solutions</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">Project Details</label>
                  <textarea 
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your excavating project..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">Maximum 500 characters</div>
                </div>
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">Thank you! Your message has been sent successfully.</p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium">Sorry, something went wrong. Please try again.</p>
                  </div>
                )}
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-8 py-4 bg-orange-600 text-white text-base font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Request Free Estimate'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-orange-600 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <i className="ri-truck-line text-xl text-orange-600"></i>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">Kader Excavating</div>
                </div>
              </div>
              <p className="text-white/90 text-sm leading-relaxed">
                Professional excavating and earthmoving services throughout Kentucky.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-base mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">Home</a></li>
                <li><a href="#services" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">Services</a></li>
                <li><a href="#projects" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">Projects</a></li>
                <li><a href="#about" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">About</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-base mb-4">Services</h4>
              <ul className="space-y-2">
                <li><span className="text-white/90 text-sm">Site Preparation</span></li>
                <li><span className="text-white/90 text-sm">Trenching & Utilities</span></li>
                <li><span className="text-white/90 text-sm">Land Clearing</span></li>
                <li><span className="text-white/90 text-sm">Drainage Solutions</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-base mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="text-white/90 text-sm">Louisville, Kentucky</li>
                <li><a href="tel:+15025551234" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">(502) 555-1234</a></li>
                <li><a href="mailto:info@kaderexcavatingky.com" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">info@kaderexcavatingky.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-white/90 text-sm mb-4 md:mb-0">
              © 2024 Kader Excavating. All rights reserved.
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/90 text-sm hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
